import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Car,
  CheckCircle2,
  ClipboardList,
  Coins,
  CreditCard,
  FileText,
  Fuel,
  LogOut,
  MapPinned,
  MessageCircle,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  UserRound,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_OPTIONS,
  DRIVER_AVAILABILITY_OPTIONS,
  DRIVER_STATUS_OPTIONS,
  EXPENSE_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_OPTIONS,
  STATUS_TONE_MAP,
  VEHICLE_AVAILABILITY_OPTIONS,
  VEHICLE_STATUS_OPTIONS,
} from "@/lib/blackride-constants";
import {
  assignBookingResources,
  buildBookingWhatsappLink,
  buildDepositRequestWhatsappLink,
  buildQuoteWhatsappLink,
  calculateMetrics,
  fetchAdminSnapshot,
  formatCurrency,
  formatDate,
  formatTime,
  saveBooking,
  saveDriver,
  saveExpense,
  saveQuote,
  saveVehicle,
  updateBookingPayment,
} from "@/lib/blackrideOperations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DASHBOARD_TABS = [
  { key: "overview", label: "Overview", icon: TrendingUp },
  { key: "bookings", label: "Bookings", icon: ClipboardList },
  { key: "quotes", label: "Quotes", icon: FileText },
  { key: "fleet", label: "Fleet", icon: Car },
  { key: "drivers", label: "Drivers", icon: Users },
  { key: "finance", label: "Finance", icon: Coins },
  { key: "reports", label: "Reports", icon: ShieldCheck },
];

function startOfToday(offsetDays = 0) {
  const now = new Date();
  now.setDate(now.getDate() + offsetDays);
  return now.toISOString().slice(0, 10);
}

function StatusBadge({ value, label }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_TONE_MAP[value] || "border-white/10 bg-white/5 text-gray-300"}`}>
      {label || BOOKING_STATUS_LABELS[value] || value || "—"}
    </span>
  );
}

function Panel({ title, description, action, children }) {
  return (
    <section className="rounded-[28px] border border-white/10 bg-[#111111] p-5 md:p-6 shadow-[0_24px_60px_rgba(0,0,0,0.18)]">
      {(title || action) && (
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            {title && <h2 className="font-[Playfair_Display] text-3xl text-white">{title}</h2>}
            {description && <p className="mt-2 text-sm text-gray-400">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function MetricCard({ icon: Icon, label, value, helper }) {
  return (
    <div className="rounded-3xl border border-white/8 bg-[#0b0b0b] p-5">
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Icon className="h-4 w-4 text-[#C7A239]" />
        <span>{label}</span>
      </div>
      <div className="mt-4 text-3xl font-semibold text-white">{value}</div>
      {helper && <p className="mt-2 text-xs text-gray-500">{helper}</p>}
    </div>
  );
}

function Field({ label, children, helper }) {
  return (
    <label className="block space-y-2 text-sm text-gray-300">
      <span>{label}</span>
      {children}
      {helper && <span className="block text-xs text-gray-500">{helper}</span>}
    </label>
  );
}

function TextInput(props) {
  return <input {...props} className={`w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60 ${props.className || ""}`} />;
}

function SelectInput(props) {
  return <select {...props} className={`w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60 ${props.className || ""}`} />;
}

function TextArea(props) {
  return <textarea {...props} className={`w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60 ${props.className || ""}`} />;
}

function formatStatusOptions(options, labels = {}) {
  return options.map((value) => ({ value, label: labels[value] || value.replaceAll("_", " ") }));
}

function blankBooking() {
  return {
    customer_name: "",
    customer_phone: "",
    customer_whatsapp: "",
    customer_email: "",
    pickup_location: "",
    drop_location: "",
    date: startOfToday(0),
    time: "10:00",
    service_type: "dropoff",
    vehicle_preference: "",
    passengers: 1,
    luggage: 0,
    duration_hours: 4,
    estimated_duration_minutes: 120,
    notes: "",
    status: "new",
    payment_status: "pending",
    payment_method: "",
    amount_paid: 0,
    quoted_amount: "",
    current_driver_id: "",
    current_vehicle_id: "",
  };
}

function bookingDraftFromRecord(record) {
  return {
    id: record.id,
    customer_name: record.customer_name || "",
    customer_phone: record.customer_phone || "",
    customer_whatsapp: record.customer_whatsapp || "",
    customer_email: record.customer_email || "",
    pickup_location: record.pickup_location || "",
    drop_location: record.drop_location || "",
    date: record.date || startOfToday(0),
    time: String(record.time || "10:00").slice(0, 5),
    service_type: record.service_type || "dropoff",
    vehicle_preference: record.vehicle_preference || "",
    passengers: record.passengers || 1,
    luggage: record.luggage || 0,
    duration_hours: record.duration_hours || 4,
    estimated_duration_minutes: record.estimated_duration_minutes || 120,
    notes: record.notes || "",
    status: record.status || "new",
    payment_status: record.payment_status || "pending",
    payment_method: record.payment_method || "",
    amount_paid: record.amount_paid || 0,
    quoted_amount: record.quoted_amount || "",
    current_driver_id: record.current_driver_id || "",
    current_vehicle_id: record.current_vehicle_id || "",
  };
}

function blankQuote(bookingId) {
  return {
    booking_id: bookingId,
    base_fare: 0,
    extra_charges: 0,
    discount: 0,
    vat_amount: 0,
    valid_until: "",
    notes: "",
  };
}

function quoteDraftFromRecord(bookingId, quote) {
  return {
    booking_id: bookingId,
    base_fare: quote?.base_fare || 0,
    extra_charges: quote?.extra_charges || 0,
    discount: quote?.discount || 0,
    vat_amount: quote?.vat_amount || 0,
    valid_until: quote?.valid_until ? String(quote.valid_until).slice(0, 16) : "",
    notes: quote?.notes || "",
  };
}

function blankVehicle() {
  return {
    vehicle_name: "",
    model: "",
    year: new Date().getFullYear(),
    plate_number: "",
    category: "",
    seats: 4,
    luggage_capacity: 2,
    status: "active",
    availability: "available",
    notes: "",
  };
}

function blankDriver() {
  return {
    auth_user_id: "",
    name: "",
    phone: "",
    license_number: "",
    languages: "Arabic, English",
    status: "active",
    assigned_vehicle_id: "",
    availability: "available",
    notes: "",
  };
}

function driverDraftFromRecord(record) {
  return {
    id: record.id,
    auth_user_id: record.auth_user_id || "",
    name: record.name || "",
    phone: record.phone || "",
    license_number: record.license_number || "",
    languages: Array.isArray(record.languages) ? record.languages.join(", ") : "",
    status: record.status || "active",
    assigned_vehicle_id: record.assigned_vehicle_id || "",
    availability: record.availability || "available",
    notes: record.notes || "",
  };
}

function vehicleDraftFromRecord(record) {
  return {
    id: record.id,
    vehicle_name: record.vehicle_name || "",
    model: record.model || "",
    year: record.year || new Date().getFullYear(),
    plate_number: record.plate_number || "",
    category: record.category || "",
    seats: record.seats || 4,
    luggage_capacity: record.luggage_capacity || 2,
    status: record.status || "active",
    availability: record.availability || "available",
    notes: record.notes || "",
  };
}

function blankExpense(bookingId = "") {
  return {
    booking_id: bookingId,
    expense_type: "fuel",
    amount: 0,
    notes: "",
    incurred_at: new Date().toISOString().slice(0, 16),
  };
}

export default function AdminDashboard() {
  const { user, profile, signOut } = useAuth();
  const [tab, setTab] = useState("overview");
  const [dateRange, setDateRange] = useState({ from: startOfToday(-30), to: startOfToday(30) });
  const [snapshot, setSnapshot] = useState({ bookings: [], quotes: [], vehicles: [], drivers: [], expenses: [] });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [bookingModal, setBookingModal] = useState({ open: false, draft: blankBooking() });
  const [quoteModal, setQuoteModal] = useState({ open: false, booking: null, draft: blankQuote("") });
  const [vehicleModal, setVehicleModal] = useState({ open: false, draft: blankVehicle() });
  const [driverModal, setDriverModal] = useState({ open: false, draft: blankDriver() });
  const [assignModal, setAssignModal] = useState({ open: false, booking: null, driverId: "", vehicleId: "", error: "" });
  const [financeModal, setFinanceModal] = useState({ open: false, booking: null, payment_status: "pending", payment_method: "", amount_paid: 0 });
  const [expenseModal, setExpenseModal] = useState({ open: false, draft: blankExpense() });

  const canManageResources = profile?.role === "admin";

  const loadSnapshot = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminSnapshot(dateRange);
      setSnapshot(data);
    } catch (loadError) {
      setError(loadError.message || "Unable to load the dashboard. Run the Supabase schema first if this is a fresh setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot();
  }, [dateRange.from, dateRange.to]);

  const metrics = useMemo(() => calculateMetrics({ ...snapshot, ...dateRange }), [snapshot, dateRange]);
  const vehiclesById = useMemo(() => new Map(snapshot.vehicles.map((vehicle) => [vehicle.id, vehicle])), [snapshot.vehicles]);
  const driversById = useMemo(() => new Map(snapshot.drivers.map((driver) => [driver.id, driver])), [snapshot.drivers]);

  const bookings = useMemo(() => snapshot.bookings.map((booking) => ({
    ...booking,
    quote: snapshot.quotes.find((quote) => quote.booking_id === booking.id) || null,
    expenses: snapshot.expenses.filter((expense) => expense.booking_id === booking.id),
    currentVehicle: booking.current_vehicle_id ? vehiclesById.get(booking.current_vehicle_id) : null,
    currentDriver: booking.current_driver_id ? driversById.get(booking.current_driver_id) : null,
  })), [snapshot, vehiclesById, driversById]);

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      if (statusFilter !== "all" && booking.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const haystack = [
        booking.booking_reference,
        booking.customer_name,
        booking.customer_phone,
        booking.pickup_location,
        booking.drop_location,
        booking.vehicle_preference,
      ].join(" ").toLowerCase();
      return haystack.includes(search.trim().toLowerCase());
    });
  }, [bookings, statusFilter, search]);

  const upcomingBookings = useMemo(() => bookings.filter((booking) => ["new", "quoted", "confirmed", "assigned", "ongoing"].includes(booking.status)).slice(0, 6), [bookings]);
  const draftQuotes = useMemo(() => bookings.filter((booking) => booking.status === "new" || booking.status === "quoted").slice(0, 8), [bookings]);
  const financeRows = useMemo(() => bookings.filter((booking) => booking.quoted_amount || booking.quote?.total_amount || booking.amount_paid), [bookings]);

  const openBookingModal = (record = null) => setBookingModal({ open: true, draft: record ? bookingDraftFromRecord(record) : blankBooking() });
  const openQuoteModal = (booking) => setQuoteModal({ open: true, booking, draft: quoteDraftFromRecord(booking.id, booking.quote) });
  const openVehicleModal = (record = null) => setVehicleModal({ open: true, draft: record ? vehicleDraftFromRecord(record) : blankVehicle() });
  const openDriverModal = (record = null) => setDriverModal({ open: true, draft: record ? driverDraftFromRecord(record) : blankDriver() });
  const openAssignModal = (booking) => setAssignModal({ open: true, booking, driverId: booking.current_driver_id || "", vehicleId: booking.current_vehicle_id || "", error: "" });
  const openFinanceModal = (booking) => setFinanceModal({ open: true, booking, payment_status: booking.payment_status || "pending", payment_method: booking.payment_method || "", amount_paid: booking.amount_paid || 0 });
  const openExpenseModal = (booking = null) => setExpenseModal({ open: true, draft: blankExpense(booking?.id || "") });

  const handleStatusUpdate = async (booking, nextStatus) => {
    setSubmitting(true);
    setError("");
    try {
      await saveBooking({ ...bookingDraftFromRecord(booking), status: nextStatus }, profile?.id || user?.id);
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || `Unable to move booking to ${nextStatus}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareDeposit = (booking) => {
    const quotedTotal = Number(booking.quoted_amount || booking.quote?.total_amount || 0);
    if (!quotedTotal) {
      setError("Create or save a quote before requesting the booking deposit.");
      return;
    }

    const paymentLinkUrl = window.prompt("Paste the payment link to send with the 50% deposit request.");
    if (!paymentLinkUrl) return;

    const whatsappLink = buildDepositRequestWhatsappLink({
      booking,
      paymentLinkUrl,
      depositAmount: quotedTotal * 0.5,
    });

    if (!whatsappLink) {
      setError("The guest does not have a WhatsApp or phone number saved yet.");
      return;
    }

    window.open(whatsappLink, "_blank", "noopener,noreferrer");
  };

  const handleBookingSave = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await saveBooking(bookingModal.draft, profile?.id || user?.id);
      setBookingModal({ open: false, draft: blankBooking() });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuoteSave = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await saveQuote(quoteModal.draft, profile?.id || user?.id);
      setQuoteModal({ open: false, booking: null, draft: blankQuote("") });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save quote.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVehicleSave = async (event) => {
    event.preventDefault();
    if (!canManageResources) return;
    setSubmitting(true);
    setError("");
    try {
      await saveVehicle(vehicleModal.draft);
      setVehicleModal({ open: false, draft: blankVehicle() });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save vehicle.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDriverSave = async (event) => {
    event.preventDefault();
    if (!canManageResources) return;
    setSubmitting(true);
    setError("");
    try {
      await saveDriver(driverModal.draft);
      setDriverModal({ open: false, draft: blankDriver() });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save driver.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignSave = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setAssignModal((current) => ({ ...current, error: "" }));
    try {
      const result = await assignBookingResources({ booking: assignModal.booking, driverId: assignModal.driverId || null, vehicleId: assignModal.vehicleId || null });
      if (!result.ok) {
        setAssignModal((current) => ({ ...current, error: result.reason }));
      } else {
        setAssignModal({ open: false, booking: null, driverId: "", vehicleId: "", error: "" });
        await loadSnapshot();
      }
    } catch (submitError) {
      setAssignModal((current) => ({ ...current, error: submitError.message || "Unable to assign the trip." }));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSave = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await updateBookingPayment(financeModal);
      setFinanceModal({ open: false, booking: null, payment_status: "pending", payment_method: "", amount_paid: 0 });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save payment status.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExpenseSave = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await saveExpense(expenseModal.draft, profile?.id || user?.id);
      setExpenseModal({ open: false, draft: blankExpense() });
      await loadSnapshot();
    } catch (submitError) {
      setError(submitError.message || "Unable to save expense.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBookingCard = (booking) => {
    const bookingWhatsappLink = buildBookingWhatsappLink({ booking });
    const quotedTotal = Number(booking.quoted_amount || booking.quote?.total_amount || 0);

    return (
    <div key={booking.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-[Playfair_Display] text-2xl text-white">{booking.customer_name || booking.booking_reference}</p>
            <StatusBadge value={booking.status} />
            <StatusBadge value={booking.payment_status} label={booking.payment_status?.replace("_", " ")} />
          </div>
          <p className="mt-2 text-sm text-gray-400">{booking.booking_reference} • {SERVICE_TYPE_LABELS[booking.service_type] || booking.service_type} • {booking.customer_phone || "No phone provided"}</p>
          <div className="mt-4 grid gap-2 text-sm text-gray-300 md:grid-cols-2">
            <p><span className="text-gray-500">Pickup:</span> {booking.pickup_location}</p>
            <p><span className="text-gray-500">Drop-off:</span> {booking.drop_location || "Hourly booking"}</p>
            <p><span className="text-gray-500">Schedule:</span> {formatDate(booking.date)} at {formatTime(booking.time)}</p>
            <p><span className="text-gray-500">Vehicle preference:</span> {booking.vehicle_preference || "Any premium vehicle"}</p>
            <p><span className="text-gray-500">Passengers / luggage:</span> {booking.passengers} / {booking.luggage}</p>
            <p><span className="text-gray-500">Quote:</span> {formatCurrency(booking.quoted_amount || booking.quote?.total_amount || 0)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end">
          {bookingWhatsappLink && (
            <a href={bookingWhatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white">
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> Guest WhatsApp
            </a>
          )}
          {(["new", "quoted"].includes(booking.status)) && (
            <button onClick={() => handleStatusUpdate(booking, "confirmed")} disabled={submitting} className="rounded-full border border-[#C7A239]/30 px-4 py-2 text-sm text-[#C7A239] disabled:opacity-60">Mark confirmed</button>
          )}
          {quotedTotal > 0 && (
            <button onClick={() => handleShareDeposit(booking)} className="rounded-full border border-[#C7A239]/30 px-4 py-2 text-sm text-[#C7A239]">Request 50% deposit</button>
          )}
          <button onClick={() => openBookingModal(booking)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white">Edit</button>
          <button onClick={() => openQuoteModal(booking)} className="rounded-full border border-[#C7A239]/30 px-4 py-2 text-sm text-[#C7A239]">Quote</button>
          <button onClick={() => openAssignModal(booking)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white">Assign</button>
          <button onClick={() => openFinanceModal(booking)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white">Finance</button>
        </div>
      </div>
      {(booking.currentDriver || booking.currentVehicle) && (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-gray-300">
            <p className="text-xs uppercase tracking-[0.24em] text-gray-500">Driver</p>
            <p className="mt-2 text-white">{booking.currentDriver?.name || "Not assigned"}</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-gray-300">
            <p className="text-xs uppercase tracking-[0.24em] text-gray-500">Vehicle</p>
            <p className="mt-2 text-white">{booking.currentVehicle ? `${booking.currentVehicle.vehicle_name} ${booking.currentVehicle.model}` : "Not assigned"}</p>
          </div>
        </div>
      )}
    </div>
  );
};

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#0a0a0a]/96 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-4 md:px-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-white.png" alt="BlackRide" className="h-8 md:h-10 w-auto object-contain" />
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-[#C7A239]">BlackRide operations</p>
              <p className="text-sm text-gray-400">Premium bookings, quotes, dispatch, and reporting</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300">
              {profile?.full_name || user?.email} • {profile?.role || "operator"}
            </div>
            <button onClick={loadSnapshot} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={signOut} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-5 py-2.5 text-sm font-semibold text-black">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 md:px-10 md:py-8">
        <div className="rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,#131313,#090909)] p-5 md:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {DASHBOARD_TABS.map((item) => {
                const Icon = item.icon;
                const active = tab === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setTab(item.key)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition ${active ? "bg-[#C7A239] text-black" : "border border-white/10 text-gray-300"}`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="From">
                <TextInput type="date" value={dateRange.from} onChange={(event) => setDateRange((current) => ({ ...current, from: event.target.value }))} />
              </Field>
              <Field label="To">
                <TextInput type="date" value={dateRange.to} onChange={(event) => setDateRange((current) => ({ ...current, to: event.target.value }))} />
              </Field>
              <Field label="Find a booking">
                <TextInput placeholder="Reference, guest, phone, route" value={search} onChange={(event) => setSearch(event.target.value)} />
              </Field>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-5 rounded-[28px] border border-red-500/20 bg-red-500/10 px-5 py-4 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-6">
          {tab === "overview" && (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard icon={ClipboardList} label="Total bookings" value={metrics.totalBookings} helper="All bookings in the selected date range" />
                <MetricCard icon={Coins} label="Total revenue" value={formatCurrency(metrics.totalRevenue)} helper={`Expenses tracked: ${formatCurrency(metrics.totalExpenses)}`} />
                <MetricCard icon={CreditCard} label="Pending payments" value={formatCurrency(metrics.pendingPayments)} helper="Outstanding balance still to be collected" />
                <MetricCard icon={CheckCircle2} label="Completed trips" value={metrics.completedTrips} helper={`Quote conversion ${metrics.quoteConversionRate.toFixed(0)}%`} />
              </div>

              <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
                <Panel title="Upcoming operations" description="Fresh booking requests, quoted trips, and active assignments needing attention.">
                  <div className="space-y-4">
                    {upcomingBookings.length === 0 ? <p className="text-sm text-gray-500">No upcoming operations in range.</p> : upcomingBookings.map(renderBookingCard)}
                  </div>
                </Panel>
                <Panel title="Operations health" description="Utilization, conversion, and premium service coverage.">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                    <MetricCard icon={Car} label="Vehicle occupancy" value={`${metrics.vehicleOccupancy.toFixed(1)}%`} helper="Estimated booked hours across the active fleet" />
                    <MetricCard icon={Users} label="Driver utilization" value={`${metrics.driverUtilization.toFixed(1)}%`} helper="Scheduled hours allocated across chauffeur availability" />
                    <MetricCard icon={FileText} label="Quote pipeline" value={draftQuotes.length} helper="Bookings still waiting on an approved quote" />
                    <MetricCard icon={MapPinned} label="Live operations" value={metrics.activeOperationsCount} helper="Confirmed, assigned, and ongoing trips" />
                  </div>
                </Panel>
              </div>
            </>
          )}

          {tab === "bookings" && (
            <Panel
              title="Booking management"
              description="Create and update premium ride requests across airport transfers, hourly service, and intercity movements."
              action={<button onClick={() => openBookingModal()} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> New booking</button>}
            >
              <div className="mb-5 flex flex-wrap gap-2">
                <button onClick={() => setStatusFilter("all")} className={`rounded-full px-4 py-2 text-sm ${statusFilter === "all" ? "bg-white text-black" : "border border-white/10 text-gray-300"}`}>All</button>
                {BOOKING_STATUS_OPTIONS.map((status) => (
                  <button key={status} onClick={() => setStatusFilter(status)} className={`rounded-full px-4 py-2 text-sm ${statusFilter === status ? "bg-[#C7A239] text-black" : "border border-white/10 text-gray-300"}`}>
                    {BOOKING_STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
              <div className="space-y-4">
                {filteredBookings.length === 0 ? <p className="text-sm text-gray-500">No bookings match the current filters.</p> : filteredBookings.map(renderBookingCard)}
              </div>
            </Panel>
          )}

          {tab === "quotes" && (
            <Panel title="Quote management" description="Price each trip, confirm it on WhatsApp, and keep the initial launch tightly concierge-led.">
              <div className="space-y-4">
                {draftQuotes.length === 0 ? <p className="text-sm text-gray-500">No pending quotes right now.</p> : draftQuotes.map((booking) => {
                  const whatsappLink = booking.quote ? buildQuoteWhatsappLink({ booking, quote: booking.quote }) : "";
                  return (
                    <div key={booking.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5 md:p-6">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="font-[Playfair_Display] text-2xl text-white">{booking.customer_name || booking.booking_reference}</p>
                            <StatusBadge value={booking.status} />
                          </div>
                          <p className="mt-2 text-sm text-gray-400">{booking.pickup_location} {booking.drop_location ? `→ ${booking.drop_location}` : ""}</p>
                          <p className="mt-2 text-sm text-gray-300">Current amount: {formatCurrency(booking.quote?.total_amount || booking.quoted_amount || 0)}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => openQuoteModal(booking)} className="rounded-full border border-[#C7A239]/30 px-4 py-2 text-sm text-[#C7A239]">{booking.quote ? "Edit quote" : "Create quote"}</button>
                          {whatsappLink && (
                            <a href={whatsappLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white">
                              <MessageCircle className="h-4 w-4 text-[#25D366]" /> Send on WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {tab === "fleet" && (
            <Panel
              title="Vehicle management"
              description="Maintain a premium-ready fleet with live availability and category-level capacity visibility."
              action={canManageResources ? <button onClick={() => openVehicleModal()} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> Add vehicle</button> : null}
            >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {snapshot.vehicles.map((vehicle) => (
                  <div key={vehicle.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-[Playfair_Display] text-2xl text-white">{vehicle.vehicle_name}</p>
                        <p className="text-sm text-gray-400">{vehicle.model} • {vehicle.year}</p>
                      </div>
                      <StatusBadge value={vehicle.availability} label={vehicle.availability?.replace("_", " ")} />
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-gray-300">
                      <p><span className="text-gray-500">Category:</span> {vehicle.category}</p>
                      <p><span className="text-gray-500">Plate:</span> {vehicle.plate_number || "—"}</p>
                      <p><span className="text-gray-500">Seats / luggage:</span> {vehicle.seats} / {vehicle.luggage_capacity}</p>
                    </div>
                    {canManageResources && <button onClick={() => openVehicleModal(vehicle)} className="mt-5 rounded-full border border-white/10 px-4 py-2 text-sm text-white">Edit vehicle</button>}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {tab === "drivers" && (
            <Panel
              title="Driver management"
              description="Manage chauffeur availability, license details, languages, and assigned premium vehicles."
              action={canManageResources ? <button onClick={() => openDriverModal()} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> Add driver</button> : null}
            >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {snapshot.drivers.map((driver) => (
                  <div key={driver.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-[Playfair_Display] text-2xl text-white">{driver.name}</p>
                        <p className="text-sm text-gray-400">{driver.phone || "No phone listed"}</p>
                      </div>
                      <StatusBadge value={driver.availability} label={driver.availability?.replace("_", " ")} />
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-gray-300">
                      <p><span className="text-gray-500">License:</span> {driver.license_number || "—"}</p>
                      <p><span className="text-gray-500">Languages:</span> {Array.isArray(driver.languages) && driver.languages.length ? driver.languages.join(", ") : "—"}</p>
                      <p><span className="text-gray-500">Linked auth user:</span> {driver.auth_user_id || "Not linked yet"}</p>
                    </div>
                    {canManageResources && <button onClick={() => openDriverModal(driver)} className="mt-5 rounded-full border border-white/10 px-4 py-2 text-sm text-white">Edit driver</button>}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {tab === "finance" && (
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <Panel title="Payments" description="Track collection status, payment method, and outstanding balances on each trip.">
                <div className="space-y-4">
                  {financeRows.length === 0 ? <p className="text-sm text-gray-500">No finance data available yet.</p> : financeRows.map((booking) => (
                    <div key={booking.id} className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="font-[Playfair_Display] text-2xl text-white">{booking.customer_name || booking.booking_reference}</p>
                          <p className="mt-2 text-sm text-gray-400">{booking.booking_reference} • {booking.payment_method || "Payment method pending"}</p>
                          <div className="mt-4 grid gap-2 text-sm text-gray-300 md:grid-cols-3">
                            <p><span className="text-gray-500">Quoted:</span> {formatCurrency(booking.quoted_amount || booking.quote?.total_amount || 0)}</p>
                            <p><span className="text-gray-500">Paid:</span> {formatCurrency(booking.amount_paid || 0)}</p>
                            <p><span className="text-gray-500">Expenses:</span> {formatCurrency(metrics.expenseByBooking.get(booking.id) || 0)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <StatusBadge value={booking.payment_status} label={booking.payment_status?.replace("_", " ")} />
                          <button onClick={() => openFinanceModal(booking)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-white">Update</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="Trip expenses" description="Track fuel, tolls, parking, and chauffeur allowances per booking." action={<button onClick={() => openExpenseModal()} className="inline-flex items-center gap-2 rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> Add expense</button>}>
                <div className="space-y-3">
                  {snapshot.expenses.length === 0 ? <p className="text-sm text-gray-500">No expenses logged yet.</p> : snapshot.expenses.slice(0, 12).map((expense) => {
                    const booking = bookings.find((item) => item.id === expense.booking_id);
                    return (
                      <div key={expense.id} className="rounded-3xl border border-white/10 bg-[#0b0b0b] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-white">{expense.expense_type.replaceAll("_", " ")}</p>
                            <p className="mt-1 text-xs text-gray-500">{booking?.booking_reference || "Booking removed"} • {formatDate(expense.incurred_at)}</p>
                          </div>
                          <p className="text-sm font-semibold text-[#C7A239]">{formatCurrency(expense.amount)}</p>
                        </div>
                        {expense.notes && <p className="mt-3 text-sm text-gray-400">{expense.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </div>
          )}

          {tab === "reports" && (
            <Panel title="Reports" description="Filterable revenue, occupancy, utilization, and conversion insights for premium BlackRide operations.">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard icon={ClipboardList} label="Bookings" value={metrics.totalBookings} helper="Within the selected range" />
                <MetricCard icon={TrendingUp} label="Revenue" value={formatCurrency(metrics.totalRevenue)} helper="Quoted/confirmed revenue" />
                <MetricCard icon={CreditCard} label="Pending payments" value={formatCurrency(metrics.pendingPayments)} helper="Still to be collected" />
                <MetricCard icon={Car} label="Vehicle occupancy" value={`${metrics.vehicleOccupancy.toFixed(1)}%`} helper="Booked hours / theoretical capacity" />
                <MetricCard icon={Users} label="Driver utilization" value={`${metrics.driverUtilization.toFixed(1)}%`} helper="Allocated hours / available hours" />
              </div>

              <div className="mt-6 grid gap-6 xl:grid-cols-2">
                <div className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5">
                  <h3 className="font-[Playfair_Display] text-2xl text-white">Revenue per vehicle</h3>
                  <div className="mt-4 space-y-3">
                    {metrics.revenuePerVehicle.filter((entry) => entry.revenue > 0).slice(0, 6).map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-gray-300">
                        <span>{entry.label}</span>
                        <span className="font-semibold text-white">{formatCurrency(entry.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-[28px] border border-white/10 bg-[#0b0b0b] p-5">
                  <h3 className="font-[Playfair_Display] text-2xl text-white">Revenue per driver</h3>
                  <div className="mt-4 space-y-3">
                    {metrics.revenuePerDriver.filter((entry) => entry.revenue > 0).slice(0, 6).map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-gray-300">
                        <span>{entry.label}</span>
                        <span className="font-semibold text-white">{formatCurrency(entry.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Panel>
          )}
        </div>
      </main>

      <Dialog open={bookingModal.open} onOpenChange={(open) => setBookingModal({ open, draft: open ? bookingModal.draft : blankBooking() })}>
        <DialogContent className="max-w-4xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">{bookingModal.draft.id ? "Edit booking" : "Create booking"}</DialogTitle>
            <DialogDescription className="text-gray-400">Capture every guest detail the operations team needs before quoting, dispatching, and closing the trip.</DialogDescription>
          </DialogHeader>
          <form className="mt-6 space-y-5" onSubmit={handleBookingSave}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Customer name"><TextInput value={bookingModal.draft.customer_name} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, customer_name: event.target.value } }))} /></Field>
              <Field label="Phone"><TextInput value={bookingModal.draft.customer_phone} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, customer_phone: event.target.value } }))} /></Field>
              <Field label="WhatsApp"><TextInput value={bookingModal.draft.customer_whatsapp} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, customer_whatsapp: event.target.value } }))} /></Field>
              <Field label="Email"><TextInput type="email" value={bookingModal.draft.customer_email} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, customer_email: event.target.value } }))} /></Field>
              <Field label="Pickup"><TextInput value={bookingModal.draft.pickup_location} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, pickup_location: event.target.value } }))} required /></Field>
              <Field label="Drop-off"><TextInput value={bookingModal.draft.drop_location} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, drop_location: event.target.value } }))} /></Field>
              <Field label="Date"><TextInput type="date" value={bookingModal.draft.date} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, date: event.target.value } }))} required /></Field>
              <Field label="Time"><TextInput type="time" value={bookingModal.draft.time} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, time: event.target.value } }))} required /></Field>
              <Field label="Service type"><SelectInput value={bookingModal.draft.service_type} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, service_type: event.target.value } }))}>{formatStatusOptions(SERVICE_TYPE_OPTIONS, SERVICE_TYPE_LABELS).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</SelectInput></Field>
              <Field label="Vehicle preference"><TextInput value={bookingModal.draft.vehicle_preference} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, vehicle_preference: event.target.value } }))} /></Field>
              <Field label="Passengers"><TextInput type="number" min="1" value={bookingModal.draft.passengers} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, passengers: event.target.value } }))} /></Field>
              <Field label="Luggage"><TextInput type="number" min="0" value={bookingModal.draft.luggage} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, luggage: event.target.value } }))} /></Field>
              {bookingModal.draft.service_type === "hourly" ? (
                <Field label="Duration hours"><TextInput type="number" min="1" value={bookingModal.draft.duration_hours} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, duration_hours: event.target.value } }))} /></Field>
              ) : (
                <Field label="Estimated duration minutes"><TextInput type="number" min="30" value={bookingModal.draft.estimated_duration_minutes} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, estimated_duration_minutes: event.target.value } }))} /></Field>
              )}
              <Field label="Status"><SelectInput value={bookingModal.draft.status} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, status: event.target.value } }))}>{formatStatusOptions(BOOKING_STATUS_OPTIONS, BOOKING_STATUS_LABELS).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</SelectInput></Field>
            </div>
            <Field label="Notes"><TextArea rows={4} value={bookingModal.draft.notes} onChange={(event) => setBookingModal((current) => ({ ...current, draft: { ...current.draft, notes: event.target.value } }))} /></Field>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : bookingModal.draft.id ? "Save booking" : "Create booking"}</button>
              <button type="button" onClick={() => setBookingModal({ open: false, draft: blankBooking() })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={quoteModal.open} onOpenChange={(open) => setQuoteModal({ open, booking: open ? quoteModal.booking : null, draft: open ? quoteModal.draft : blankQuote("") })}>
        <DialogContent className="max-w-2xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">Quote booking</DialogTitle>
            <DialogDescription className="text-gray-400">Build a premium quote with base fare, surcharges, discount, VAT, validity, and operational notes.</DialogDescription>
          </DialogHeader>
          <form className="mt-6 space-y-5" onSubmit={handleQuoteSave}>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Base fare"><TextInput type="number" min="0" step="0.01" value={quoteModal.draft.base_fare} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, base_fare: event.target.value } }))} /></Field>
              <Field label="Extra charges"><TextInput type="number" min="0" step="0.01" value={quoteModal.draft.extra_charges} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, extra_charges: event.target.value } }))} /></Field>
              <Field label="Discount"><TextInput type="number" min="0" step="0.01" value={quoteModal.draft.discount} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, discount: event.target.value } }))} /></Field>
              <Field label="VAT / tax"><TextInput type="number" min="0" step="0.01" value={quoteModal.draft.vat_amount} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, vat_amount: event.target.value } }))} /></Field>
              <Field label="Valid until"><TextInput type="datetime-local" value={quoteModal.draft.valid_until} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, valid_until: event.target.value } }))} /></Field>
              <Field label="Quoted total"><TextInput value={formatCurrency(Number(quoteModal.draft.base_fare || 0) + Number(quoteModal.draft.extra_charges || 0) - Number(quoteModal.draft.discount || 0) + Number(quoteModal.draft.vat_amount || 0))} readOnly /></Field>
            </div>
            <Field label="Quote notes"><TextArea rows={4} value={quoteModal.draft.notes} onChange={(event) => setQuoteModal((current) => ({ ...current, draft: { ...current.draft, notes: event.target.value } }))} /></Field>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : "Save quote"}</button>
              <button type="button" onClick={() => setQuoteModal({ open: false, booking: null, draft: blankQuote("") })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={vehicleModal.open} onOpenChange={(open) => setVehicleModal({ open, draft: open ? vehicleModal.draft : blankVehicle() })}>
        <DialogContent className="max-w-2xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">{vehicleModal.draft.id ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
          </DialogHeader>
          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={handleVehicleSave}>
            <Field label="Vehicle name"><TextInput value={vehicleModal.draft.vehicle_name} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, vehicle_name: event.target.value } }))} required /></Field>
            <Field label="Model"><TextInput value={vehicleModal.draft.model} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, model: event.target.value } }))} required /></Field>
            <Field label="Year"><TextInput type="number" value={vehicleModal.draft.year} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, year: event.target.value } }))} /></Field>
            <Field label="Plate number"><TextInput value={vehicleModal.draft.plate_number} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, plate_number: event.target.value } }))} /></Field>
            <Field label="Category"><TextInput value={vehicleModal.draft.category} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, category: event.target.value } }))} required /></Field>
            <Field label="Seats"><TextInput type="number" min="1" value={vehicleModal.draft.seats} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, seats: event.target.value } }))} /></Field>
            <Field label="Luggage capacity"><TextInput type="number" min="0" value={vehicleModal.draft.luggage_capacity} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, luggage_capacity: event.target.value } }))} /></Field>
            <Field label="Status"><SelectInput value={vehicleModal.draft.status} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, status: event.target.value } }))}>{VEHICLE_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Availability"><SelectInput value={vehicleModal.draft.availability} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, availability: event.target.value } }))}>{VEHICLE_AVAILABILITY_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Notes" helper="Garage notes or vehicle-specific operational remarks"><TextArea rows={4} value={vehicleModal.draft.notes} onChange={(event) => setVehicleModal((current) => ({ ...current, draft: { ...current.draft, notes: event.target.value } }))} /></Field>
            <div className="md:col-span-2 flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : "Save vehicle"}</button>
              <button type="button" onClick={() => setVehicleModal({ open: false, draft: blankVehicle() })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={driverModal.open} onOpenChange={(open) => setDriverModal({ open, draft: open ? driverModal.draft : blankDriver() })}>
        <DialogContent className="max-w-2xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">{driverModal.draft.id ? "Edit driver" : "Add driver"}</DialogTitle>
            <DialogDescription className="text-gray-400">Link an auth user ID when you want this chauffeur to access the mobile driver portal.</DialogDescription>
          </DialogHeader>
          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={handleDriverSave}>
            <Field label="Auth user ID"><TextInput value={driverModal.draft.auth_user_id} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, auth_user_id: event.target.value } }))} /></Field>
            <Field label="Name"><TextInput value={driverModal.draft.name} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, name: event.target.value } }))} required /></Field>
            <Field label="Phone"><TextInput value={driverModal.draft.phone} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, phone: event.target.value } }))} /></Field>
            <Field label="License number"><TextInput value={driverModal.draft.license_number} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, license_number: event.target.value } }))} /></Field>
            <Field label="Languages"><TextInput value={driverModal.draft.languages} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, languages: event.target.value } }))} helper="Comma-separated, for example Arabic, English, Urdu" /></Field>
            <Field label="Assigned vehicle"><SelectInput value={driverModal.draft.assigned_vehicle_id} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, assigned_vehicle_id: event.target.value } }))}><option value="">No vehicle linked</option>{snapshot.vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.vehicle_name} {vehicle.model}</option>)}</SelectInput></Field>
            <Field label="Status"><SelectInput value={driverModal.draft.status} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, status: event.target.value } }))}>{DRIVER_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Availability"><SelectInput value={driverModal.draft.availability} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, availability: event.target.value } }))}>{DRIVER_AVAILABILITY_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Notes"><TextArea rows={4} value={driverModal.draft.notes} onChange={(event) => setDriverModal((current) => ({ ...current, draft: { ...current.draft, notes: event.target.value } }))} /></Field>
            <div className="md:col-span-2 flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : "Save driver"}</button>
              <button type="button" onClick={() => setDriverModal({ open: false, draft: blankDriver() })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignModal.open} onOpenChange={(open) => setAssignModal({ open, booking: open ? assignModal.booking : null, driverId: open ? assignModal.driverId : "", vehicleId: open ? assignModal.vehicleId : "", error: "" })}>
        <DialogContent className="max-w-xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">Assign driver and vehicle</DialogTitle>
            <DialogDescription className="text-gray-400">Assignments are checked against overlapping schedules before they are saved.</DialogDescription>
          </DialogHeader>
          <form className="mt-6 space-y-4" onSubmit={handleAssignSave}>
            <Field label="Driver">
              <SelectInput value={assignModal.driverId} onChange={(event) => setAssignModal((current) => ({ ...current, driverId: event.target.value }))}>
                <option value="">Choose a driver</option>
                {snapshot.drivers.filter((driver) => driver.status === "active").map((driver) => <option key={driver.id} value={driver.id}>{driver.name} • {driver.availability.replaceAll("_", " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Vehicle">
              <SelectInput value={assignModal.vehicleId} onChange={(event) => setAssignModal((current) => ({ ...current, vehicleId: event.target.value }))}>
                <option value="">Choose a vehicle</option>
                {snapshot.vehicles.filter((vehicle) => vehicle.status === "active").map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.vehicle_name} {vehicle.model} • {vehicle.availability.replaceAll("_", " ")}</option>)}
              </SelectInput>
            </Field>
            {assignModal.error && <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{assignModal.error}</div>}
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Assigning..." : "Save assignment"}</button>
              <button type="button" onClick={() => setAssignModal({ open: false, booking: null, driverId: "", vehicleId: "", error: "" })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={financeModal.open} onOpenChange={(open) => setFinanceModal({ open, booking: open ? financeModal.booking : null, payment_status: open ? financeModal.payment_status : "pending", payment_method: open ? financeModal.payment_method : "", amount_paid: open ? financeModal.amount_paid : 0 })}>
        <DialogContent className="max-w-xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">Update payment</DialogTitle>
          </DialogHeader>
          <form className="mt-6 space-y-4" onSubmit={handlePaymentSave}>
            <Field label="Payment status"><SelectInput value={financeModal.payment_status} onChange={(event) => setFinanceModal((current) => ({ ...current, payment_status: event.target.value }))}>{PAYMENT_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Payment method"><SelectInput value={financeModal.payment_method} onChange={(event) => setFinanceModal((current) => ({ ...current, payment_method: event.target.value }))}><option value="">Select method</option>{PAYMENT_METHOD_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Amount paid so far"><TextInput type="number" min="0" step="0.01" value={financeModal.amount_paid} onChange={(event) => setFinanceModal((current) => ({ ...current, amount_paid: event.target.value }))} /></Field>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : "Save payment"}</button>
              <button type="button" onClick={() => setFinanceModal({ open: false, booking: null, payment_status: "pending", payment_method: "", amount_paid: 0 })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={expenseModal.open} onOpenChange={(open) => setExpenseModal({ open, draft: open ? expenseModal.draft : blankExpense() })}>
        <DialogContent className="max-w-xl rounded-[28px] border-white/10 bg-[#0b0b0b] text-white">
          <DialogHeader>
            <DialogTitle className="font-[Playfair_Display] text-3xl text-white">Add expense</DialogTitle>
          </DialogHeader>
          <form className="mt-6 space-y-4" onSubmit={handleExpenseSave}>
            <Field label="Booking"><SelectInput value={expenseModal.draft.booking_id} onChange={(event) => setExpenseModal((current) => ({ ...current, draft: { ...current.draft, booking_id: event.target.value } }))}><option value="">Select booking</option>{bookings.map((booking) => <option key={booking.id} value={booking.id}>{booking.booking_reference} • {booking.customer_name || booking.pickup_location}</option>)}</SelectInput></Field>
            <Field label="Expense type"><SelectInput value={expenseModal.draft.expense_type} onChange={(event) => setExpenseModal((current) => ({ ...current, draft: { ...current.draft, expense_type: event.target.value } }))}>{EXPENSE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}</SelectInput></Field>
            <Field label="Amount"><TextInput type="number" min="0" step="0.01" value={expenseModal.draft.amount} onChange={(event) => setExpenseModal((current) => ({ ...current, draft: { ...current.draft, amount: event.target.value } }))} /></Field>
            <Field label="Incurred at"><TextInput type="datetime-local" value={expenseModal.draft.incurred_at} onChange={(event) => setExpenseModal((current) => ({ ...current, draft: { ...current.draft, incurred_at: event.target.value } }))} /></Field>
            <Field label="Notes"><TextArea rows={4} value={expenseModal.draft.notes} onChange={(event) => setExpenseModal((current) => ({ ...current, draft: { ...current.draft, notes: event.target.value } }))} /></Field>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">{submitting ? "Saving..." : "Save expense"}</button>
              <button type="button" onClick={() => setExpenseModal({ open: false, draft: blankExpense() })} className="rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">Cancel</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
